import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import { Loading, InformationMessageToast } from "../../components/atoms";
import isLoggedIn from "../../common/helpers/isUserLoggedIn";
// import BehalfOrglField from "./components/BehalfOrgIdField";
import AssertionCriteria from "./components/AssertionCriteria";
import ClinicalSignificance from "./components/ClinicalSignificance";
import ConditionSet from "./components/ConditionSet";
import ObservedIn from "./components/ObservedIn";
import SubmissionName from "./components/SubmissionName";
import VariantSet from "./components/VariantSet";
import RecordStatus from "./components/RecordStatus";
import ReleaseStatus from "./components/ReleaseStatus";
import {
  submitToClinvarUsingSubmissionId,
  updateClinvarSubBySubmissionId,
} from "./services/clinvar-api";
import {
  ClinvarFormError,
  ClinvarApiError,
} from "./services/clinvar-error-classes";
import AddToast from "../../state/actions/AddToast";
import { getVariantId } from "../../state/selectors/general.selectors";
import {
  clean,
  getWrongFields,
  getErrors,
  formValidation,
  STATUS_CODES,
} from "./helpers/helpers";
import { validationErrors } from "./constants/validationErrors";
import LocalId from "./components/LocalId";
import LocalKey from "./components/LocalKey";
import { ChromosomeCoordinatesType, ClassificationType } from "./types/types";
import { setSubmissionRefresh } from "../../state/actions/FormActions";
import { InputHelperText } from "../../components/atoms/Form";
import HelperText from "../../components/atoms/HelperText";
import { Descriptions } from "./constants/descriptions";
import DrugResponse from "./components/DrugResponse";
import DrugResponseItems from "./helpers/DrugResponseItems";

const DRUG_RESPONSE = "drug response";

const ClinvarForm = (props) => {
  const variantId = useSelector(getVariantId);
  const isUserLoggedIn = isLoggedIn();
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);
  const {
    setOpen,
    classification = null,
    hgvs,
    chromosomeCoordinates,
    submissionErrors,
    submissionId,
    status: formStatus,
    refreshState,
    clinvarAccession,
    isEdited,
    statusCode,
  } = props;

  const [userCanSubmit, setUserCanSubmit] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [drugResponse, setDrugResponse] = useState(
    classification?.clinvarSubmission?.[0]?.conditionSet?.drugResponse?.length >
      0
      ? classification?.clinvarSubmission?.[0]?.conditionSet?.drugResponse.map(
          (x) => ({
            db: x.db || "",
            id: x.id || "",
            drugName: x.drugName || "",
            condition:
              x?.condition?.length > 0
                ? x.condition.map((y) => ({
                    db: y.db || "",
                    id: y.id || "",
                    name: y.name || "",
                  }))
                : [
                    {
                      db: "",
                      id: "",
                      name: "",
                    },
                  ],
          })
        )
      : [
          {
            db: "",
            id: "",
            drugName: "",
            condition: [
              {
                db: "",
                id: "",
                name: "",
              },
            ],
          },
        ]
  );
  const drugItems = useMemo(() => new DrugResponseItems(drugResponse), []);

  const [touched, setTouched] = useState({
    recordStatus: false,
    clinicalSignificance: {
      clinicalSignificanceDescription: false,
      dateLastEvaluated: false,
      customAssertionScore: false,
      citation: (classification &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation
          ?.length > 0 &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation.map(
          () => ({
            id: ``,
            url: ``,
            db: ``,
          })
        )) || [
        {
          id: false,
          url: false,
          db: false,
        },
      ],
    },
    variant: [
      {
        hgvs: false,
        chromosomeCoordinates: {
          assembly: false,
          chromosome: false,
          start: false,
          stop: false,
        },
        gene: (classification?.clinvarSubmission[0]?.variantSet?.variant[0]
          ?.gene &&
          classification?.clinvarSubmission[0]?.variantSet?.variant[0]?.gene
            ?.length > 0 &&
          classification?.clinvarSubmission[0]?.variantSet?.variant[0].gene.map(
            () => ({
              id: false,
            })
          )) || [
          {
            id: false,
          },
        ],
      },
    ],
    assertionCriteria: {
      url: false,
      db: false,
      id: false,
    },
    conditionSet: {
      condition: (classification?.clinvarSubmission[0]?.conditionSet
        ?.condition &&
        classification?.clinvarSubmission[0]?.conditionSet?.condition.length >
          0 &&
        classification?.clinvarSubmission[0]?.conditionSet?.condition.map(
          () => ({
            name: false,
            id: false,
            db: false,
          })
        )) || [
        {
          name: false,
          id: false,
          db: false,
        },
      ],
      multipleConditionExplanation: false,
    },
    observedIn: (classification?.clinvarSubmission[0]?.observedIn &&
      classification?.clinvarSubmission[0]?.observedIn?.length > 0 &&
      classification?.clinvarSubmission[0]?.observedIn?.map((val) => ({
        affectedStatus: false,
        alleleOrigin: false,
        collectionMethod: false,
        numberOfIndividuals: false,
        clinicalFeatures: val?.clinicalFeatures?.length
          ? val?.clinicalFeatures.map(() => ({
              db: false,
              id: false,
              clinicalFeaturesAffectedStatus: false,
              name: false,
            }))
          : [],
      }))) || [
      {
        affectedStatus: false,
        alleleOrigin: false,
        collectionMethod: false,
        numberOfIndividuals: false,
        clinicalFeatures: [
          // {
          //   clinicalFeaturesAffectedStatus: false,
          // },
        ],
      },
    ],
  });

  const [variantSet, setvariantSet] = useState({
    variantSet: {
      variant: [
        {
          chromosomeCoordinates: {
            // accession: "",
            alternateAllele: "",
            assembly: "",
            chromosome: "",
            // innerStart: 0,
            // innerStop: 0,
            // outerStart: 0,
            // outerStop: 0,
            referenceAllele: "",
            start: "",
            stop: "",
            // variantLength: 0,
          },
          // copyNumber: "",
          gene: [
            {
              id: "",
              symbol: "",
            },
          ],
          hgvs: "",
          // referenceCopyNumber: 0,
          variantType: "",
        },
      ],
    },
  });

  const [errors, setErrors] = useState({
    recordStatus: "",
    clinicalSignificance: {
      clinicalSignificanceDescription: classification?.clinvarSubmission[0]
        ?.clinicalSignificance?.clinicalSignificanceDescription
        ? ""
        : validationErrors.emptyStringError,
      dateLastEvaluated: ``,
      customAssertionScore: ``,
      citation: (classification &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation
          .length > 0 &&
        classification?.clinvarSubmission[0]?.clinicalSignificance?.citation.map(
          () => ({
            id: ``,
            url: ``,
            db: ``,
          })
        )) || [
        {
          id: validationErrors.emptyStringError,
          url: validationErrors.emptyStringError,
          db: validationErrors.emptyStringError,
        },
      ],
    },
    variant: [
      {
        hgvs:
          classification?.clinvarSubmission[0]?.variantSet?.variant[0] ||
          variantSet?.variantSet?.variant[0]?.hgvs
            ? ""
            : validationErrors.emptyStringError,
        chromosomeCoordinates: {
          assembly:
            classification?.clinvarSubmission[0]?.variantSet?.variant[0]
              ?.chromosomeCoordinates?.assembly ||
            variantSet?.variantSet?.variant[0]?.chromosomeCoordinates?.assembly
              ? ""
              : validationErrors.emptyStringError,
          chromosome:
            classification?.clinvarSubmission[0]?.variantSet?.variant[0]
              ?.chromosomeCoordinates?.chromosome ||
            variantSet?.variantSet?.variant[0]?.chromosomeCoordinates
              ?.chromosome
              ? ""
              : validationErrors.emptyStringError,
          start: ``,
          stop: ``,
        },
        gene: (classification?.clinvarSubmission[0]?.variantSet?.variant[0]
          ?.gene &&
          classification?.clinvarSubmission[0]?.variantSet?.variant[0]?.gene
            .length > 0 &&
          classification?.clinvarSubmission[0]?.variantSet?.variant[0]?.gene?.map(
            () => ({
              id: ``,
            })
          )) || [
          {
            id: ``,
          },
        ],
      },
    ],
    assertionCriteria: {
      url:
        classification?.assertionCriteria?.url ||
        (classification?.assertionCriteria?.id &&
          classification?.assertionCriteria?.db)
          ? ""
          : validationErrors.emptyStringError,
      id:
        classification?.assertionCriteria?.id &&
        !classification?.assertionCriteria?.url
          ? ""
          : validationErrors.emptyStringError,
      db:
        classification?.assertionCriteria?.db &&
        !classification?.assertionCriteria?.url
          ? ""
          : validationErrors.emptyStringError,
    },
    conditionSet: {
      condition: (classification?.clinvarSubmission[0]?.conditionSet
        ?.condition &&
        classification?.clinvarSubmission[0]?.conditionSet?.condition.length >
          0 &&
        classification?.clinvarSubmission[0]?.conditionSet?.condition.map(
          () => ({
            name: "",
            id: "",
            db: "",
          })
        )) || [
        {
          name: validationErrors.emptyStringError,
          id: validationErrors.emptyStringError,
          db: validationErrors.emptyStringError,
        },
      ],
      // multipleConditionExplanation: classification?.clinvarSubmission[0]
      //   ?.conditionSet.multipleConditionExplanation
      //   ? ""
      //   : validationErrors.emptyStringError,
    },
    observedIn: (classification?.clinvarSubmission[0]?.observedIn &&
      classification?.clinvarSubmission[0]?.observedIn.length > 0 &&
      classification?.clinvarSubmission[0]?.observedIn?.map((val) => ({
        affectedStatus: "",
        alleleOrigin: "",
        collectionMethod: "",
        numberOfIndividuals: "",
        clinicalFeatures: val?.clinicalFeatures?.length
          ? val?.clinicalFeatures.map((field) => ({
              db: "",
              id: "",
              clinicalFeaturesAffectedStatus:
                field?.clinicalFeaturesAffectedStatus
                  ? ""
                  : validationErrors.emptyStringError,
              name: "",
            }))
          : [],
      }))) || [
      {
        affectedStatus: validationErrors.emptyStringError,
        alleleOrigin: validationErrors.emptyStringError,
        collectionMethod: validationErrors.emptyStringError,
        numberOfIndividuals: ``,
        clinicalFeatures: [
          // {
          //   clinicalFeaturesAffectedStatus: "",
          // },
        ],
      },
    ],
  });
  const [observedIn, setobservedIn] = useState([
    {
      affectedStatus: "",
      alleleOrigin: "",
      clinicalFeatures: [
        {
          db: "",
          id: "",
          name: "",
          clinicalFeaturesAffectedStatus: "",
        },
      ],
      clinicalFeaturesComment: "",
      collectionMethod: "",
      numberOfIndividuals: "",
      structVarMethodType: "",
    },
  ]);

  const [conditionSet, setconditionSet] = useState({
    condition: [
      {
        db: "",
        name: "",
        id: "",
      },
    ],
  });

  const [uniqueParams, setUniqueParams] = useState({
    // behalfOrgId: classification?.behalfOrgID || "",
    clinvartoken: "",
    submissionName: classification?.submissionName || "",
    localid: classification?.clinvarSubmission[0]?.localID || "",
    localKey: classification?.clinvarSubmission[0]?.localKey || "",
    clinvarAccession:
      clinvarAccession ||
      classification?.clinvarSubmission[0]?.clinvarAccession ||
      "",
    recordStatus: classification?.clinvarSubmission[0]?.recordStatus || "",
    releaseStatus: classification?.clinvarSubmissionReleaseStatus || "",
  });

  const [assertionCriteria, setAssertionCriteria] = useState({
    db: "",
    id: "",
    url: "",
  });

  const [clinicalSignificance, setClinicalSignificance] = useState({
    citation: [
      {
        id: "",
        url: "",
        db: "",
      },
    ],
    modeOfInheritance: "",
    comment: "",
    dateLastEvaluated: "",
    clinicalSignificanceDescription: "",
  });

  useEffect(() => {
    const { clinvarAccession: clinvarAccessionUnique } = uniqueParams;
    let newUniqueParams = { ...uniqueParams };
    if (!statusCode) {
      newUniqueParams = {
        ...newUniqueParams,
        recordStatus: "novel",
      };
      setUniqueParams(newUniqueParams);
      return;
    }
    if (statusCode === STATUS_CODES.ERROR && !clinvarAccessionUnique) {
      newUniqueParams = {
        ...newUniqueParams,
        recordStatus: "novel",
      };
      setUniqueParams(newUniqueParams);
      return;
    }
    if (statusCode === STATUS_CODES.ERROR && clinvarAccessionUnique) {
      newUniqueParams = {
        ...newUniqueParams,
        recordStatus: "update",
      };
      setUniqueParams(newUniqueParams);
      return;
    }
    if (
      statusCode === STATUS_CODES.PUBLISHED_ON_CLINVAR ||
      statusCode === STATUS_CODES.PROCESSED_NOT_PUBLISHED
    ) {
      newUniqueParams = {
        ...newUniqueParams,
        recordStatus: "update",
      };
      setUniqueParams(newUniqueParams);
    }
  }, [formStatus, classification]);
  useEffect(() => {
    if (classification) {
      const newclinicalSignificance = { ...clinicalSignificance };
      merge(
        newclinicalSignificance,
        classification.clinvarSubmission[0].clinicalSignificance
      );
      setClinicalSignificance(newclinicalSignificance);

      const newAssertionCriteria = { ...assertionCriteria };
      merge(newAssertionCriteria, classification?.assertionCriteria);
      setAssertionCriteria(newAssertionCriteria);

      const newObservedIn = { ...observedIn };
      merge(newObservedIn, classification.clinvarSubmission[0].observedIn);
      setobservedIn(newObservedIn);

      const newvariantSet = { ...variantSet };
      merge(
        newvariantSet.variantSet,
        classification.clinvarSubmission[0].variantSet
      );
      setvariantSet(newvariantSet);

      const newConditionSet = { ...conditionSet };
      merge(conditionSet, classification.clinvarSubmission[0].conditionSet);
      setconditionSet(newConditionSet);
    }
  }, [classification]);

  const handleSubmit = () => {
    const isDrugResponseActive =
      clinicalSignificance.clinicalSignificanceDescription === DRUG_RESPONSE;
    setTouched({ ...getWrongFields(touched) });
    if (isDrugResponseActive) {
      drugItems.setAllTouched();
    }
    // Check if errors exist before sending the object (should be done through validations)
    // After constructing the object for the final request we remove all keys from the object(s) that have
    // empty string values. This must be done since the API does not accept these empty inputs.
    // Ex -> Citation should only have database and id but OR url but url="" should not be present at the
    // nested citation object. This is not safe yet for submissions but it will be working perfectly after
    // validations are done and make sure that all required fields are not "".
    setSubmitErrorMessage(null);

    const submission = {
      classification: {
        behalfOrgID: parseInt(uniqueParams?.behalfOrgId, 10) || "",
        clinvarSubmission: [
          {
            clinicalSignificance,
            clinvarAccession: uniqueParams.clinvarAccession,
            conditionSet: isDrugResponseActive
              ? { drugResponse }
              : conditionSet,
            localID: uniqueParams.localid,
            localKey: uniqueParams.localKey,
            observedIn,
            recordStatus: uniqueParams.recordStatus,
            variantSet: variantSet.variantSet,
          },
        ],
        assertionCriteria,
        clinvarSubmissionReleaseStatus: uniqueParams.releaseStatus,
        submissionName: uniqueParams.submissionName,
      },
      variant_id: variantId,
    };

    const fixedSubmission = cloneDeep(submission);
    const finalsubm = clean(fixedSubmission);

    let finalErrors;
    if (isDrugResponseActive && errors?.conditionSet?.condition?.length > 0) {
      const newErrors = cloneDeep(errors);
      newErrors.conditionSet.condition = newErrors.conditionSet.condition.map(
        () => ({
          db: false,
          id: false,
          name: false,
        })
      );
      finalErrors = newErrors;
    } else {
      finalErrors = errors;
    }

    if (
      getErrors(cloneDeep(finalErrors)) ||
      (isDrugResponseActive && drugItems.hasErrors())
    ) {
      setUserCanSubmit(false);
      return;
    }

    setUserCanSubmit(true);
    setIsLoading(true);
    setUserCanSubmit(true);
    const result =
      statusCode === STATUS_CODES.PUBLISHED_ON_CLINVAR ||
      statusCode === STATUS_CODES.PROCESSED_NOT_PUBLISHED ||
      statusCode === STATUS_CODES.ERROR
        ? updateClinvarSubBySubmissionId(submissionId, finalsubm)
        : submitToClinvarUsingSubmissionId(finalsubm);
    result
      .then(() => {
        AddToast("The form has been successfully submitted", "success");
        setIsLoading(false);
        setSubmissionRefresh(refreshState + 1);
        setOpen(false);
      })
      .catch((error) => {
        if (error instanceof ClinvarFormError) {
          setSubmitErrorMessage(JSON.stringify(error.message));
        } else if (error instanceof ClinvarApiError) {
          setSubmitErrorMessage(
            `There was an error on CliVar API while submitting the form: ${JSON.stringify(
              error.message
            )}`
          );
        } else {
          setSubmitErrorMessage(
            `There was an error submitting the form: ${error.message}`
          );
        }
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const validate = formValidation;

  const handleBlur = (evt, index = null) => {
    const { name, value, id } = evt.target;
    if (name === "assembly" || name === "chromosome") {
      const newErrors = { ...errors };
      const newTouched = { ...touched };
      newErrors.variant[0].chromosomeCoordinates[name] = "";
      newTouched.variant[0].chromosomeCoordinates[name] = true;
      const error = validate.variant[0].chromosomeCoordinates[name](value);
      newErrors.variant[0].chromosomeCoordinates[name] = error;
      setTouched(newTouched);
      setErrors(newErrors);
      return;
    }

    if (name === "hgvs") {
      const newErrors = { ...errors };
      const newTouched = { ...touched };
      newErrors.variant[0].hgvs = "";
      newTouched.variant[0].hgvs = true;
      const error = validate.variant[0][name](value);
      newErrors.variant[0].hgvs = error;
      setTouched(newTouched);
      setErrors(newErrors);
      return;
    }

    if (id.includes("geneid")) {
      const newErrors = { ...errors };
      newErrors.variant[0].gene[index][name] = "";
      setErrors(newErrors);
      const newTouched = { ...touched };
      newTouched.variant[0].gene[index][name] = true;
      setTouched(newTouched);
      const error = validate.variant[0].gene[0][name](value);
      newErrors.variant[0].gene[index][name] = error;
      setErrors(newErrors);
      return;
    }

    if (
      id.includes("affectedStatus") ||
      id.includes("alleleOrigin") ||
      id.includes("collectionMethod") ||
      id.includes("numberOfIndividuals")
    ) {
      const newErrors = { ...errors };
      newErrors.observedIn[index][name] = "";
      setErrors(newErrors);
      const newTouched = { ...touched };
      newTouched.observedIn[index][name] = true;
      setTouched(newTouched);
      const error = validate.observedIn[0][name](value);
      newErrors.observedIn[index][name] = error;
      setErrors(newErrors);
      return;
    }

    if (
      id === "assertionCriteriaDb" ||
      id === "assertionCriteriaId" ||
      id === "assertionCriteriaUrl"
    ) {
      const newErrors = { ...errors };
      newErrors.assertionCriteria[name] = "";
      setErrors(newErrors);
      const newTouchedState = { ...touched };
      newTouchedState.assertionCriteria[name] = true;
      setTouched(newTouchedState);
      const error = validate.assertionCriteria[name](value);
      newErrors.assertionCriteria[name] = error;
      setErrors(newErrors);
      return;
    }
    if (
      id.includes("conditionSetName") ||
      id.includes("conditionSetId") ||
      id.includes("conditionSetDb")
    ) {
      const newstate = { ...errors };
      newstate.conditionSet.condition[index][name] = "";
      setErrors(newstate);

      const newTouchedState = { ...touched };
      newTouchedState.conditionSet.condition[index][name] = true;
      setTouched(newTouchedState);

      const error = validate.conditionSet.condition[0][name](value);
      newstate.conditionSet.condition[index][name] = error;
      setErrors(newstate);
      return;
    }
    if (id === "assertionCriteriaUrl") {
      setErrors({
        ...errors,
        assertionCriteria: { ...errors.assertionCriteria, [name]: "" },
      });
      setTouched({
        ...touched,
        assertionCriteria: { ...touched.assertionCriteria, [name]: true },
      });
      const error = validate.assertionCriteria[name](value);
      setErrors({
        ...errors,
        assertionCriteria: { ...errors.assertionCriteria, [name]: error },
      });
      return;
    }

    if (
      name === "clinicalSignificanceDescription" ||
      name === "dateLastEvaluated" ||
      name === "customAssertionScore" ||
      id.includes("clinicalSignificanceurl") ||
      id.includes("clinicalSignificanceid") ||
      id.includes("clinicalSignificancedb")
    ) {
      if (name === "url" || name === "db" || name === "id") {
        if (
          value === "" &&
          clinicalSignificance.citation[index].url === "" &&
          clinicalSignificance.citation[index].id === "" &&
          clinicalSignificance.citation[index].db === ""
        ) {
          const newErrors = { ...errors };
          newErrors.clinicalSignificance.citation[index].db = "";
          newErrors.clinicalSignificance.citation[index].id = "";
          newErrors.clinicalSignificance.citation[index].url = "";
          setErrors(newErrors);
          return;
        }

        const newErrors = { ...errors };
        newErrors.clinicalSignificance.citation[index][name] = "";
        setErrors(newErrors);
        const newTouched = { ...touched };
        newTouched.clinicalSignificance.citation[index][name] = true;
        setTouched(newTouched);
        const error = validate.clinicalSignificance.citation[0][name](value);
        newErrors.clinicalSignificance.citation[index][name] = error;
        setErrors(newErrors);
        return;
      }

      setErrors({
        ...errors,
        clinicalSignificance: { ...errors.clinicalSignificance, [name]: "" },
      });
      setTouched({
        ...touched,
        clinicalSignificance: { ...touched.clinicalSignificance, [name]: true },
      });
      const error = validate.clinicalSignificance[name](value);
      setErrors({
        ...errors,
        clinicalSignificance: { ...errors.clinicalSignificance, [name]: error },
      });
      return;
    }
    if (name === "start" || name === "stop") {
      const newerrorState = { ...errors };
      newerrorState.variant[0].chromosomeCoordinates[name] = "";
      setErrors(newerrorState);
      const newTouchedstate = { ...touched };
      newTouchedstate.variant[0].chromosomeCoordinates[name] = true;
      setTouched(newTouchedstate);
      const error = validate.variant[0].chromosomeCoordinates[name](value);
      newerrorState.variant[0].chromosomeCoordinates[name] = error;
      setErrors(newerrorState);
      return;
    }

    setTouched({
      ...touched,
      [name]: true,
    });
    const newstate = { ...errors };
    const error = validate[name](value);
    newstate[name] = error;
    setErrors(newstate);
  };

  const onChangeObserveIn = (object) => {
    setobservedIn(object);
  };

  const onChangeConditionSet = (object) => {
    setconditionSet(object);
  };

  const onChangeAssertionCriteria = (e) => {
    const { name, value } = e.target;

    const assertionCriteriaFields = {
      ...assertionCriteria,
      [name]: e.target.value,
    };

    if (value === "") {
      if (name === "db" && assertionCriteria.id === "") {
        const newErrors = { ...errors };
        newErrors.assertionCriteria.url = validationErrors.emptyStringError;
        setErrors(newErrors);
      } else if (name === "id" && assertionCriteria.db === "") {
        const newErrors = { ...errors };
        newErrors.assertionCriteria.url = validationErrors.emptyStringError;
        setErrors(newErrors);
      } else if (
        name === "url" &&
        assertionCriteria.db === "" &&
        assertionCriteria.id === ""
      ) {
        const newErrors = { ...errors };
        newErrors.assertionCriteria.db = validationErrors.emptyStringError;
        newErrors.assertionCriteria.id = validationErrors.emptyStringError;
        newErrors.assertionCriteria.url = validationErrors.emptyStringError;
        setErrors(newErrors);
      }
    }
    if (
      (name === "db" || name === "id") &&
      value !== "" &&
      errors.assertionCriteria.url !== ""
    ) {
      const newErrors = { ...errors };
      newErrors.assertionCriteria.url = "";
      setErrors(newErrors);
    } else if (
      name === "url" &&
      value !== "" &&
      (errors.assertionCriteria.id !== "" || errors.assertionCriteria.db !== "")
    ) {
      const newErrors = { ...errors };
      newErrors.assertionCriteria.id = "";
      newErrors.assertionCriteria.db = "";
      setErrors(newErrors);
    }
    setAssertionCriteria(assertionCriteriaFields);
  };

  const onChangeClinicalSign = (object) => {
    setClinicalSignificance(object);
  };

  const onVariantChange = (object) => {
    setvariantSet(object);
  };

  const onChange = (value, name) => {
    const newstate = { ...uniqueParams };
    newstate[name] = value;
    setUniqueParams(newstate);
  };

  return (
    <div>
      {isUserLoggedIn && (
        <React.Fragment>
          <div className="tw-text-3xl">ClinVar Submission</div>
          <div className="tw-text-sm">
            Fill out the form in order to submit to ClinVar
            <span>
              <InputHelperText>
                <HelperText
                  RenderContent={() => (
                    <div>
                      {Descriptions.clinvarMainDescription.slice(0, 180)}
                    </div>
                  )}
                />
              </InputHelperText>
            </span>
          </div>
          {statusCode === STATUS_CODES.SUBMITTED && (
            <InformationMessageToast
              type="warning"
              message="Your ClinVar submission is on submitted status (not processed yet) you need to wait for ClinVar to process it in order to edit."
            />
          )}
          {isEdited && (
            <InformationMessageToast
              type="warning"
              message="You have already edited and re-submitted an edited form."
            />
          )}
          {submissionErrors && submissionErrors.length > 0 && (
            <InformationMessageToast
              type="warning"
              message={
                <React.Fragment>
                  ClinVar processed your submission and reported the following
                  errors. Please fix them and try to submit the form again.
                  {submissionErrors.map((s) => (
                    <div key={submissionErrors}>{s}</div>
                  ))}
                </React.Fragment>
              }
            />
          )}
          <form className="tw-pt-10 tw-pb-10">
            {/* <BehalfOrglField */}
            {/*  onChange={onChange} */}
            {/*  behalfOrgId={uniqueParams.behalfOrgId} */}
            {/* /> */}
            {/* <hr /> */}
            <AssertionCriteria
              onChangeAssertionCriteria={onChangeAssertionCriteria}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              assertionCriteria={assertionCriteria}
              setErrors={setErrors}
            />
            <hr />
            <ClinicalSignificance
              onChageClinicalSign={onChangeClinicalSign}
              onClinvarAccesion={onChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              setTouched={setTouched}
              setErrors={setErrors}
              defaults={
                classification?.clinvarSubmission[0]?.clinicalSignificance
              }
              fetchedClinvarAccession={uniqueParams.clinvarAccession}
              recordStatus={uniqueParams.recordStatus}
              formStatus={formStatus}
            />
            <hr />
            {clinicalSignificance.clinicalSignificanceDescription !==
            "drug response" ? (
              <ConditionSet
                onChangeConditionSet={onChangeConditionSet}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
                defaults={classification?.clinvarSubmission[0]?.conditionSet}
                userCanSubmit={userCanSubmit}
                setErrors={setErrors}
                setTouched={setTouched}
              />
            ) : (
              <DrugResponse
                data={drugResponse}
                setData={setDrugResponse}
                drugItems={drugItems}
              />
            )}
            <hr />
            <LocalId onChange={onChange} userlocalId={uniqueParams.localid} />
            <LocalKey onChange={onChange} localKey={uniqueParams.localKey} />
            <hr />
            <ObservedIn
              setErrors={setErrors}
              setTouched={setTouched}
              onChangeObserveIn={onChangeObserveIn}
              observedIn={classification?.clinvarSubmission[0]?.observedIn}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
            <SubmissionName
              onChange={onChange}
              usersubmissionName={uniqueParams.submissionName}
            />
            <RecordStatus
              onChange={onChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              recordStatus={uniqueParams.recordStatus}
              isEditMode={Boolean(classification)}
              formStatus={formStatus}
              statusCode={statusCode}
              clinvarAccession={uniqueParams.clinvarAccession}
            />
            <ReleaseStatus
              onChange={onChange}
              releaseStatus={uniqueParams.releaseStatus}
            />
            <hr />
            <VariantSet
              formStatus={formStatus}
              statusCode={statusCode}
              onVariantChange={onVariantChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              hgvs={hgvs}
              setTouched={setTouched}
              setErrors={setErrors}
              chrCoords={chromosomeCoordinates}
              defaults={classification?.clinvarSubmission[0]?.variantSet}
            />
            <hr />
            {submitErrorMessage && (
              <InformationMessageToast
                type="error"
                message={submitErrorMessage}
              />
            )}
            {!userCanSubmit && (
              <InformationMessageToast
                type="error"
                message="You cannot submit yet. You must fill all the required fields"
              />
            )}
            <div className="tw-flex tw-m-4 tw-justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-lg btn-success"
                disabled={
                  isLoading ||
                  (statusCode &&
                    (statusCode === STATUS_CODES.SUBMITTED ||
                      statusCode === STATUS_CODES.PROCESSING ||
                      isEdited))
                }
              >
                {isLoading ? <Loading /> : "Submit"}
              </button>
            </div>
          </form>
        </React.Fragment>
      )}
    </div>
  );
};

ClinvarForm.propTypes = {
  setOpen: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  submissionErrors: PropTypes.arrayOf(PropTypes.string),
  classification: ClassificationType,
  hgvs: PropTypes.string,
  chromosomeCoordinates: ChromosomeCoordinatesType,
  submissionId: PropTypes.string,
  refreshState: PropTypes.number,
  clinvarAccession: PropTypes.string,
  status: PropTypes.string,
  statusCode: PropTypes.number,
  isEdited: PropTypes.bool,
};

ClinvarForm.defaultProps = {
  chromosomeCoordinates: null,
  isEdited: false,
  submissionErrors: [],
  hgvs: null,
  submissionId: null,
  statusCode: 0,
  classification: null,
  refreshState: 0,
  clinvarAccession: null,
  status: "",
};

export default ClinvarForm;
